import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { map } from 'rxjs';

import { UI, createServerSearch, type UiOption } from '../../components/ui';
import {
  MailingService,
  choiceValue,
  type AudiencePreview,
  type AudienceScope,
  type CampaignCategory,
  type CategoryKind,
  type FromAudienceInput,
  type MailingOption,
} from '../../services/mailing.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { apiErrorMessage } from '../campaign-create/campaign-create.component';

export type ComposerMode = 'marketing' | 'announcement' | 'welcome';

const MERCHANT_ROLES = ['MERCHANT_ADMIN', 'MERCHANT_FINANCIAL', 'MERCHANT_DEVELOPER'];
const SYSTEM_ROLES = ['SYSTEM_ADMIN', 'SYSTEM_FINANCIAL', 'SYSTEM_DEVELOPER'];

/**
 * Compose an audience-based mailing: audience builder, content fields, preview (sandboxed iframe) and send.
 * One component for the Marketing, Announcements and Welcome rollout tabs (`mode`).
 */
@Component({
  selector: 'app-mailing-composer',
  standalone: true,
  imports: [FormsModule, RouterLink, ...UI],
  template: `
    @if (error()) {
      <ui-alert variant="danger" class="mb-3 block">{{ error() }}</ui-alert>
    }
    @if (sentId(); as id) {
      <ui-alert variant="success" class="mb-3 block">
        {{ sentText() }}
        <a
          class="ml-1 text-brand hover:underline"
          [routerLink]="shellRoutes.commands('manage', 'campaigns', id)"
        >
          Open the campaign
        </a>
      </ui-alert>
    }

    @if (mode === 'welcome') {
      <p class="mb-3 text-sm text-muted">
        Sends the welcome information email to every user once. Passwords are never included. If a
        welcome rollout already exists you will be asked to confirm before sending it again.
      </p>
    }

    <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
      @if (mode === 'announcement') {
        <ui-form-field label="Announcement type">
          <select
            uiInput
            [ngModel]="announceKind()"
            (ngModelChange)="setAnnounceKind($event)"
            name="announceKind"
          >
            <option value="PROVIDER_ADDED">Provider added</option>
            <option value="MARKET_ADDED">Market added</option>
          </select>
        </ui-form-field>
        @if (announceKind() === 'PROVIDER_ADDED') {
          <ui-form-field label="Provider">
            <ui-autocomplete
              placeholder="Select a provider" ariaLabel="Provider"
              [options]="providerSearch.options()" [loading]="providerSearch.loading()" [filterLocal]="false"
              [ngModel]="entityId" (ngModelChange)="entityId = $event ?? ''" name="entityId"
              (search)="providerSearch.search($event)"
            />
          </ui-form-field>
        } @else {
          <ui-form-field label="Market (country)">
            <ui-autocomplete
              placeholder="Select a market" ariaLabel="Market"
              [options]="marketSearch.options()" [loading]="marketSearch.loading()" [filterLocal]="false"
              [ngModel]="countryId" (ngModelChange)="countryId = $event ?? ''" name="countryId"
              (search)="marketSearch.search($event)"
            />
          </ui-form-field>
        }
      }

      <ui-form-field [label]="mode === 'welcome' ? 'Welcome category' : 'Category'">
        <select
          uiInput
          [ngModel]="categoryId()"
          (ngModelChange)="setCategory($event)"
          name="category"
        >
          @for (c of categories(); track c.id) {
            <option [value]="c.id">{{ c.name }}</option>
          } @empty {
            <option value="">No active category of this kind</option>
          }
        </select>
      </ui-form-field>

      @if (mode !== 'welcome') {
        <ui-form-field label="Campaign name (optional)">
          <input uiInput type="text" [(ngModel)]="name" name="name" />
        </ui-form-field>
      }
    </div>

    @if (mode !== 'welcome') {
      <fieldset class="mb-4 rounded-md border border-border p-3">
        <legend class="px-1 text-sm font-semibold text-fg">Audience</legend>
        <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <ui-form-field label="Send to">
            <select uiInput [ngModel]="scope()" (ngModelChange)="setScope($event)" name="scope">
              <option value="merchants">Merchants (one email per merchant)</option>
              <option value="users">Users (one email per user)</option>
            </select>
          </ui-form-field>
          <ui-form-field label="Roles">
            <div class="flex flex-wrap gap-x-4 gap-y-1">
              @for (r of availableRoles(); track r) {
                <label class="flex items-center gap-1.5 text-sm text-fg">
                  <input type="checkbox" [checked]="roles().includes(r)" (change)="toggleRole(r)" />
                  {{ r }}
                </label>
              }
            </div>
          </ui-form-field>
          <ui-form-field
            class="md:col-span-2"
            label="Limit to merchants (optional)"
            hint="Leave empty for all merchants."
          >
            <ui-multi-autocomplete
              [options]="merchantOptions()"
              [loading]="merchantsLoading()"
              [filterLocal]="false"
              placeholder="Search merchants"
              ariaLabel="Merchants"
              [ngModel]="merchantIds()"
              (ngModelChange)="merchantIds.set($event ?? [])"
              name="merchantIds"
              (search)="searchMerchants($event)"
            />
          </ui-form-field>
          <label class="flex items-center gap-2 text-sm text-fg md:col-span-2">
            <input
              type="checkbox"
              [ngModel]="includeNotification()"
              (ngModelChange)="includeNotification.set($event)"
              name="incl"
            />
            Also send to the merchant notification email
          </label>
        </div>
      </fieldset>

      <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
        <ui-form-field
          class="md:col-span-2"
          [label]="
            mode === 'marketing' ? 'Subject' : 'Subject (optional, defaults to the category)'
          "
        >
          <input uiInput type="text" [(ngModel)]="subject" name="subject" />
        </ui-form-field>
        <ui-form-field label="Heading (optional)">
          <input uiInput type="text" [(ngModel)]="heading" name="heading" />
        </ui-form-field>
        <span class="hidden md:block"></span>
        <ui-form-field
          class="md:col-span-2"
          [label]="mode === 'marketing' ? 'Body (HTML)' : 'Body (HTML, optional)'"
        >
          <textarea
            uiInput
            rows="8"
            [(ngModel)]="bodyHtml"
            name="bodyHtml"
            class="font-mono text-xs"
          ></textarea>
        </ui-form-field>
        <ui-form-field label="Button URL (optional)">
          <input uiInput type="url" [(ngModel)]="ctaUrl" name="ctaUrl" placeholder="https://" />
        </ui-form-field>
        <ui-form-field label="Button label (optional)">
          <input uiInput type="text" [(ngModel)]="ctaLabel" name="ctaLabel" />
        </ui-form-field>
      </div>
    }

    <div class="flex flex-wrap gap-2">
      <button
        type="button"
        uiButton
        variant="outline"
        [disabled]="!canSubmit() || busy()"
        (click)="preview()"
      >
        Preview
      </button>
      <button
        type="button"
        uiButton
        [disabled]="!canSubmit() || busy()"
        (click)="confirmSend.set(true)"
      >
        {{ mode === 'welcome' ? 'Send welcome to all users' : 'Send now' }}
      </button>
    </div>

    <ui-modal
      [open]="previewOpen()"
      title="Email preview"
      size="xl"
      (closed)="previewOpen.set(false)"
    >
      @if (previewData(); as pv) {
        <p class="mb-2 text-sm">
          <strong>{{ pv.recipient_count }}</strong> recipient(s). Subject:
          <strong>{{ pv.subject }}</strong>
        </p>
        @if (sampleText(pv)) {
          <p class="mb-2 text-xs text-muted">Sample: {{ sampleText(pv) }}</p>
        }
        <iframe
          title="Email preview"
          sandbox=""
          class="h-[60dvh] w-full rounded-md border border-border bg-white"
          [srcdoc]="previewHtml()"
        ></iframe>
      }
      <ng-container modal-footer>
        <button type="button" uiButton variant="outline" (click)="previewOpen.set(false)">
          Close
        </button>
      </ng-container>
    </ui-modal>

    <ui-confirm-dialog
      [open]="confirmSend()"
      title="Send this mailing?"
      [message]="confirmText()"
      confirmLabel="Send"
      (confirmed)="send(false)"
      (cancelled)="confirmSend.set(false)"
    />
    <ui-confirm-dialog
      [open]="confirmForce()"
      title="Welcome rollout already sent"
      message="A welcome rollout already exists. Send it again to every user?"
      [danger]="true"
      confirmLabel="Send again"
      (confirmed)="send(true)"
      (cancelled)="confirmForce.set(false)"
    />
  `,
})
export class MailingComposerComponent implements OnInit {
  @Input() mode: ComposerMode = 'marketing';

  private readonly svc = inject(MailingService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly shellRoutes = inject(ShellRoutesService);

  name = '';
  subject = '';
  heading = '';
  bodyHtml = '';
  ctaUrl = '';
  ctaLabel = '';
  entityId = '';
  countryId = '';

  readonly categories = signal<CampaignCategory[]>([]);
  readonly categoryId = signal('');
  readonly announceKind = signal<CategoryKind>('PROVIDER_ADDED');
  readonly scope = signal<AudienceScope>('merchants');
  readonly roles = signal<string[]>(['MERCHANT_ADMIN']);
  readonly includeNotification = signal(false);
  readonly merchantIds = signal<string[]>([]);
  readonly merchantOptions = signal<UiOption<string>[]>([]);
  readonly merchantsLoading = signal(false);
  readonly providerSearch = createServerSearch<MailingOption>({
    fetch: (term) => this.svc.providers(term).pipe(map((page) => page.results)),
    toOption: (row) => ({ value: row.id, label: row.name }),
    selected: () => (this.entityId ? [this.entityId] : []),
  });
  readonly marketSearch = createServerSearch<MailingOption>({
    fetch: (term) => this.svc.markets(term).pipe(map((page) => page.results)),
    toOption: (row) => ({ value: row.id, label: row.name }),
    selected: () => (this.countryId ? [this.countryId] : []),
  });
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  readonly sentId = signal<string | null>(null);
  readonly sentText = signal('');
  readonly previewOpen = signal(false);
  readonly previewData = signal<AudiencePreview | null>(null);
  readonly confirmSend = signal(false);
  readonly confirmForce = signal(false);

  readonly availableRoles = computed(() =>
    this.scope() === 'users' ? [...MERCHANT_ROLES, ...SYSTEM_ROLES] : MERCHANT_ROLES,
  );
  readonly previewHtml = computed<SafeHtml>(() =>
    // Only for the sandboxed iframe srcdoc: the HTML comes from our own backend renderer.
    this.sanitizer.bypassSecurityTrustHtml(this.previewData()?.html ?? ''),
  );
  readonly canSubmit = computed(() => {
    if (!this.categoryId()) return false;
    if (this.mode === 'welcome') return true;
    return this.roles().length > 0;
  });
  readonly confirmText = computed(() =>
    this.mode === 'welcome'
      ? 'The welcome email will be sent to all users now.'
      : 'The email will be sent to the selected audience now. Use Preview first to check the recipient count.',
  );

  ngOnInit(): void {
    if (this.mode === 'announcement') {
      this.providerSearch.search('');
      this.marketSearch.search('');
    }
    if (this.mode !== 'welcome') {
      this.searchMerchants('');
    }
    this.loadCategories();
  }

  setAnnounceKind(kind: CategoryKind): void {
    this.announceKind.set(kind);
    this.loadCategories();
  }

  setCategory(id: string): void {
    this.categoryId.set(id);
    const audience = this.categories().find((c) => c.id === id)?.audience;
    if (audience && this.mode !== 'welcome') {
      if (audience.scope) this.scope.set(audience.scope);
      if (audience.roles?.length) this.roles.set([...audience.roles]);
      this.includeNotification.set(!!audience.include_notification_email);
    }
  }

  setScope(scope: AudienceScope): void {
    this.scope.set(scope);
    const allowed = this.availableRoles();
    this.roles.update((r) => r.filter((x) => allowed.includes(x)));
  }

  toggleRole(role: string): void {
    this.roles.update((r) => (r.includes(role) ? r.filter((x) => x !== role) : [...r, role]));
  }

  searchMerchants(term: string): void {
    this.merchantsLoading.set(true);
    this.svc.merchants(term).subscribe({
      next: (page) => {
        const selected = this.merchantOptions().filter((o) => this.merchantIds().includes(o.value));
        const fresh = page.results.map((m) => ({ value: m.id, label: m.name }));
        const merged = [
          ...selected,
          ...fresh.filter((o) => !selected.some((s) => s.value === o.value)),
        ];
        this.merchantOptions.set(merged);
        this.merchantsLoading.set(false);
      },
      error: () => this.merchantsLoading.set(false),
    });
  }

  sampleText(pv: AudiencePreview): string {
    return pv.sample_recipients
      .map((r) => (typeof r === 'string' ? r : ((r as Record<string, unknown>)['email'] ?? '')))
      .filter(Boolean)
      .join(', ');
  }

  preview(): void {
    this.busy.set(true);
    this.error.set(null);
    this.svc.audiencePreview(this.request(false, false)).subscribe({
      next: (pv) => {
        this.previewData.set(pv);
        this.previewOpen.set(true);
        this.busy.set(false);
      },
      error: (err) => this.fail(err, 'The preview could not be generated.'),
    });
  }

  send(force: boolean): void {
    this.confirmSend.set(false);
    this.confirmForce.set(false);
    this.busy.set(true);
    this.error.set(null);
    this.svc.fromAudience(this.request(true, force)).subscribe({
      next: (campaign) => {
        this.busy.set(false);
        this.sentId.set(campaign.id);
        this.sentText.set(`Sending started for "${campaign.name}".`);
      },
      error: (err) => {
        if (err instanceof HttpErrorResponse && err.status === 409 && !force) {
          this.busy.set(false);
          this.confirmForce.set(true);
          return;
        }
        this.fail(err, 'The mailing could not be sent.');
      },
    });
  }

  private request(start: boolean, force: boolean): FromAudienceInput {
    const body: FromAudienceInput = { category: this.categoryId(), start };
    if (force) body.force = true;
    if (this.mode === 'welcome') return body;
    if (this.name.trim()) body.name = this.name.trim();
    body.overrides = {
      audience: {
        scope: this.scope(),
        roles: this.roles(),
        include_notification_email: this.includeNotification(),
        merchant_ids: this.merchantIds(),
      },
    };
    const params: NonNullable<FromAudienceInput['params']> = {};
    if (this.subject.trim()) params.subject = this.subject.trim();
    if (this.heading.trim()) params.heading = this.heading.trim();
    if (this.bodyHtml.trim()) params.body_html = this.bodyHtml;
    if (this.ctaUrl.trim()) params.cta_url = this.ctaUrl.trim();
    if (this.ctaLabel.trim()) params.cta_label = this.ctaLabel.trim();
    if (this.mode === 'announcement') {
      if (this.announceKind() === 'PROVIDER_ADDED' && this.entityId)
        params.entity_id = this.entityId;
      if (this.announceKind() === 'MARKET_ADDED' && this.countryId)
        params.country_id = this.countryId;
    }
    body.params = params;
    return body;
  }

  private kind(): CategoryKind {
    if (this.mode === 'welcome') return 'WELCOME_INFO';
    if (this.mode === 'announcement') return this.announceKind();
    return 'MARKETING';
  }

  private loadCategories(): void {
    const kind = this.kind();
    this.svc.categories(kind).subscribe({
      next: (page) => {
        const list = page.results.filter((c) => choiceValue(c.kind) === kind);
        this.categories.set(list);
        this.setCategory(list[0]?.id ?? '');
      },
      error: () => {
        this.categories.set([]);
        this.error.set('Could not load the categories.');
      },
    });
  }

  private fail(err: unknown, fallback: string): void {
    this.busy.set(false);
    this.error.set(apiErrorMessage(err, fallback));
  }
}
